// Divergent evolution (monorail-axe) — the tamagotchi read.
//
// The player no longer climbs one linear ladder "toward Heck"; instead it
// *speciates* into one of several cute critters, chosen as a readout of how it
// lived. This module owns the model, not the visuals:
//   • a random starting nudge (per-form seed) so runs vary,
//   • behavioral signals accumulated over tiers 1→2 (diet, verbs, biome dwell),
//   • a deterministic weighted-argmax resolver run at the tier-3 speciation.
// Rendering the chosen form is a later phase (player.slang bodyForm switch).

import type { Entity, Lineage, PlayerForm, LineageSignal } from '../components'
import type { EventBus } from '../events'
import type { System } from '../scheduler'
import { currentBiome } from '../biomes'
import { applyFormEyes } from './eyes'
import { T } from '../tunables'

// Runtime mirror of the PlayerForm union (components.ts). Order is irrelevant to
// the resolver (it's an argmax), but keep it aligned with the union.
export const PLAYER_FORMS = [
  'whiskerkit', 'narwhalen', 'anemonaut', 'frillgill', 'lanternet',
] as const satisfies readonly PlayerForm[]

// Shader-side form ids, packed into the player instance's mutationFlags
// bits 8..10 (render.ts). 0 is reserved for the unspeciated amoeba, and every
// id must fit in 3 bits — player.slang reads `(mflags >> 8) & 0x7`.
export const FORM_ID: Record<PlayerForm, number> = {
  whiskerkit: 1, narwhalen: 2, anemonaut: 3, frillgill: 4, lanternet: 5,
}

// Player-facing critter names. Code ids stay lowercase-stable (persisted +
// shader-packed); these are the display labels for narrator/gallery/bestiary.
export const FORM_DISPLAY: Record<PlayerForm, string> = {
  whiskerkit: 'Whiskerkit',
  narwhalen: 'Narwhalen',
  anemonaut: 'Anemonaut',
  frillgill: 'Frillgill',
  lanternet: 'Lanternet',
}

/** Eased 0→1 speciation reveal for the shader (smoothstep over the ramp).
 *  undefined revealElapsed = reveal already complete (save restore, tests). */
export function formRevealValue(lin: Lineage): number {
  if (!lin.form) return 0
  if (lin.revealElapsed === undefined) return 1
  const t = Math.min(1, lin.revealElapsed / T.lineage.revealDurationSec)
  return t * t * (3 - 2 * t)
}

function emptySignals(): Record<LineageSignal, number> {
  return {
    predation: 0, grazing: 0, aggression: 0,
    stillness: 0, deepDwell: 0, mushDwell: 0, adaptations: 0,
  }
}

/** Fresh lineage with a small random nudge per form. Pass a seeded `rng` in tests. */
export function initLineage(rng: () => number = Math.random): Lineage {
  const seed = {} as Record<PlayerForm, number>
  for (const f of PLAYER_FORMS) seed[f] = rng() * T.lineage.seedJitter
  return { seed, signals: emptySignals() }
}

/** Weighted argmax: seed + generalist baseline + Σ affinity × normalized signal.
 *  Deterministic given the lineage — the run's whole story collapses to one pick. */
export function resolveForm(lin: Lineage): PlayerForm {
  const weights = T.lineage.weights as Record<PlayerForm, Record<string, number>>
  const ref = T.lineage.refScale as Record<LineageSignal, number>
  let best: PlayerForm = 'whiskerkit'
  let bestScore = -Infinity
  for (const form of PLAYER_FORMS) {
    let score = lin.seed[form]
    if (form === 'whiskerkit') score += T.lineage.whiskerkitBase
    const w = weights[form]
    for (const sig in w) {
      const s = sig as LineageSignal
      score += w[sig] * (lin.signals[s] / ref[s])
    }
    if (score > bestScore) { bestScore = score; best = form }
  }
  return best
}

/** Accumulate behavioral signals and lock the form at the speciation tier. */
export function createLineageSystem(events: EventBus): System {
  let player: Entity | null = null
  // The still-open lineage (present, not yet speciated), or null.
  const open = (): Lineage | null =>
    player && player.lineage && !player.lineage.form ? player.lineage : null

  // Diet: predators fire both predator.eaten AND creature.eaten, so grazing
  // only counts non-predator victims.
  events.subscribe('predator.eaten', () => { const l = open(); if (l) l.signals.predation++ })
  events.subscribe('creature.eaten', e => {
    const l = open(); if (l && !e.victim.predator) l.signals.grazing++
  })
  events.subscribe('mutation.applied', () => { const l = open(); if (l) l.signals.adaptations++ })
  // Speciation locks at the BURST beat — the fanfare's peak — so the critter
  // emerges mid-moment: eyes snap open as the new form and the reveal ramp
  // (revealElapsed → shader grow-in) runs through the settle. The completed
  // subscriber is an idempotent fallback (open() is null once form is set)
  // for any morph that somehow skips its burst frame.
  const lock = (tier: number) => {
    const l = open()
    if (!l || tier < T.lineage.speciationTier || !player) return
    l.form = resolveForm(l)
    l.revealElapsed = 0
    // The critter wears its own eyes from the moment it speciates.
    applyFormEyes(player)
    events.emit({ type: 'lineage.speciated', form: l.form, player })
  }
  events.subscribe('evolution.burst', e => lock(e.tier))
  events.subscribe('evolution.completed', e => lock(e.tier))

  return {
    name: 'lineage',
    stage: 'effects',
    order: 8.5,
    run: ctx => {
      player = ctx.playerRef.current
      // Post-speciation: advance the reveal ramp (render reads it eased via
      // formRevealValue). Cheap and self-terminating.
      const lin = player?.lineage
      if (lin?.form && lin.revealElapsed !== undefined && lin.revealElapsed < T.lineage.revealDurationSec) {
        lin.revealElapsed += ctx.dt
      }
      const l = open()
      if (!l) return
      const p = player!
      const v = p.velocity
      const sp = v ? Math.hypot(v.x, v.y) : 0
      if (sp < T.lineage.stillSpeed) l.signals.stillness += ctx.dt
      if ((p.dash?.activeRemaining ?? 0) > 0 || (p.engulf?.activeRemaining ?? 0) > 0) {
        l.signals.aggression += ctx.dt
      }
      const biome = currentBiome(p)
      if (biome === 'deep') l.signals.deepDwell += ctx.dt
      else if (biome === 'mushroom') l.signals.mushDwell += ctx.dt
    },
  }
}
